import { FC, useState, type FormEvent, useMemo } from "react";
import {
    Button,
    Input,
    Label,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DataTable,
    Column,
    PhoneInput,
} from "@racoon/ui";
import { IcPlus, IcPencil, IcAiOn } from "@racoon/icons";
import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { useTableSettings } from "~/shared/hooks/useTableSettings";

import {
    useListContactsQuery,
    useCreateContactMutation,
    useUpdateContactMutation,
    useDeleteContactMutation,
} from "~/shared/api/contact/contact.grpc";

/**
 * Contacts here come from the gRPC client, so this is the proto-es shape —
 * camelCase fields and bigint ids — not the REST `Contact` of the old backend.
 * Locally staged contacts are cast into it with a negative placeholder id.
 */
type Contact = ConnectProto.Contact;

export const CustomerContactsBlock: FC<{
    organizationId?: number;
    primaryContactId?: number;
    pendingContacts?: Contact[];
    setPendingContacts?: (contacts: Contact[] | ((prev: Contact[]) => Contact[])) => void;
    onPrimaryChange?: (contactId: number) => void;
}> = ({
          organizationId,
          primaryContactId,
          onPrimaryChange,
          pendingContacts = [],
          setPendingContacts,
      }) => {
    const t = useT();
    const { columnConfig, handleColumnConfigChange } = useTableSettings(
        "customer_contacts_block",
        organizationId || undefined,
    );

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [formData, setFormData] = useState<Partial<Contact>>({
        position: "",
        name: "",
        phone: "",
        email: "",
    });

    const { data: contactsResponse, isLoading } = useListContactsQuery(
        { ownerOrganizationIds: organizationId ? [BigInt(organizationId)] : [] },
        { skip: !organizationId }
    );

    const apiContacts = contactsResponse?.contacts || [];
    const allContacts = [...apiContacts, ...pendingContacts];

    // No container groups means a contact of the customer as a whole, which is
    // every contact this tab shows.
    const globalContacts = useMemo(() => {
        return allContacts.filter((contact) => contact.containerGroupIds.length === 0);
    }, [allContacts]);

    const [createContact, { isLoading: isCreating }] = useCreateContactMutation();
    const [updateContact, { isLoading: isUpdating }] = useUpdateContactMutation();
    const [deleteContact, { isLoading: isDeleting }] = useDeleteContactMutation();

    const isLoadingGlobal = isCreating || isUpdating || isDeleting;

    const resetForm = () => {
        setFormData({ name: "", position: "", phone: "", email: "" });
        setEditingContact(null);
    };

    const handleCreate = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const handleEdit = (contact: Contact) => {
        setEditingContact(contact);
        setFormData({
            position: contact.position || "",
            name: contact.name,
            phone: contact.phone || "",
            email: contact.email || "",
        });
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!editingContact) return;

        try {
            if (editingContact.id < 0 && setPendingContacts) {
                setPendingContacts((prev) => prev.filter((c) => c.id !== editingContact.id));
            } else {
                // owner_organization_id is required by ContactRequest: the
                // permission check authorizes against it, and the delete
                // additionally filters on it. Take it from the contact itself.
                await deleteContact({
                    id: BigInt(editingContact.id),
                    ownerOrganizationId: BigInt(editingContact.ownerOrganizationId),
                }).unwrap();
            }
            setIsModalOpen(false);
            resetForm();
        } catch (error) {
            console.error("Failed to delete contact:", error);
        }
    };

    const handleSave = async (e: FormEvent) => {
        e.preventDefault();
        if (!formData.name) return;

        if (!organizationId && setPendingContacts) {
            const tempId = -Date.now();
            const newContact = {
                id: tempId,
                ...formData,
                ownerOrganizationId: 0,
                containerGroupIds: [],
            } as any;

            setPendingContacts((prev) => [...prev, newContact]);

            if (!primaryContactId && onPrimaryChange) {
                onPrimaryChange(tempId);
            }

            setIsModalOpen(false);
            resetForm();
            return;
        }

        try {
            if (editingContact) {
                // Same as delete: the permission check reads
                // owner_organization_id off the request.
                await updateContact({
                    id: BigInt(editingContact.id),
                    ownerOrganizationId: BigInt(editingContact.ownerOrganizationId),
                    name: formData.name,
                    phone: formData.phone,
                    email: formData.email,
                    position: formData.position,
                }).unwrap();
            } else {
                const newContact = await createContact({
                    ownerOrganizationId: BigInt(organizationId!),
                    name: formData.name,
                    phone: formData.phone,
                    email: formData.email,
                    position: formData.position,
                }).unwrap();

                if (!primaryContactId && newContact?.id && onPrimaryChange) {
                    onPrimaryChange(Number(newContact.id));
                }
            }
            setIsModalOpen(false);
            resetForm();
        } catch (error) {
            console.error("Failed to save contact:", error);
        }
    };

    const columns: Column<Contact>[] = useMemo(
        () => [
            {
                id: "position",
                header: t(Namespace.PickupLocation, "tablePosition"),
                accessor: (row) => row.position,
            },
            {
                id: "name",
                header: t(Namespace.PickupLocation, "tableFullName"),
                accessor: (row) => row.name,
            },
            {
                id: "phone",
                header: t(Namespace.PickupLocation, "tableContactPhone"),
                accessor: (row) => row.phone,
                cell: (info) => {
                    const phone = info.row.original.phone;
                    if (!phone) return "—";
                    return (
                        <a
                            href={`https://wa.me/${phone.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-brand-blue hover:underline"
                        >
                            {phone}
                        </a>
                    );
                },
            },
            {
                id: "priority",
                header: () => <div className="items-center flex justify-center"><IcAiOn className="w-6 h-6" /></div>,
                cell: (info) => {
                    const contact = info.row.original;
                    const isPrimary = primaryContactId === Number(contact.id);
                    return (
                        <div className="flex items-center justify-center">
                            <input
                                type="radio"
                                name="customer-primary-contact"
                                className="w-4 h-4 cursor-pointer accent-brand-blue"
                                checked={isPrimary}
                                onChange={() => onPrimaryChange?.(Number(contact.id))}
                            />
                        </div>
                    );
                },
                width: 60,
            },
            {
                id: "actions",
                header: "",
                cell: (info) => (
                    <button
                        type="button"
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors ml-auto"
                        onClick={() => handleEdit(info.row.original)}
                    >
                        <IcPencil width={18} height={18} className="text-brand-blue" />
                    </button>
                ),
                width: 50,
            },
        ],
        [t, primaryContactId, onPrimaryChange],
    );

    return (
        <div className="w-full h-full flex flex-col gap-4 overflow-y-auto pr-2 pb-10">
            <div className="flex justify-center mt-2">
                <Button
                    type="button"
                    variant="ghost"
                    className="text-brand-blue hover:bg-brand-blue/10 gap-1"
                    onClick={handleCreate}
                >
                    <IcPlus className="w-5 h-5" />
                    {t(Namespace.PickupLocation, "newContact")}
                </Button>
            </div>

            <div className="border border-gray-100 rounded-lg overflow-y-auto bg-white">
                <DataTable
                    columns={columns}
                    data={globalContacts}
                    loading={isLoading}
                    empty={<div className="p-8 text-center text-muted-foreground">{t(Namespace.PickupLocation, "noContacts")}</div>}
                    columnConfig={columnConfig}
                    onColumnConfigChange={handleColumnConfigChange}
                    namespace={Namespace.PickupLocation}
                    className="border-0"
                />
            </div>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="w-full sm:w-[40rem]">
                    <DialogHeader className="!p-0">
                        <DialogTitle>
                            {editingContact ? t(Namespace.PickupLocation, "editContact") : t(Namespace.PickupLocation, "newContact")}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        <div className="space-y-2">
                            <Label>{t(Namespace.PickupLocation, "fullName")}</Label>
                            <Input value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} required />
                        </div>
                        <div className="space-y-2">
                            <Label>{t(Namespace.PickupLocation, "position")}</Label>
                            <Input value={formData.position} onChange={(e) => setFormData((p) => ({ ...p, position: e.target.value }))} />
                        </div>
                        <div className="space-y-2">
                            <Label>{t(Namespace.PickupLocation, "contactPhone")}</Label>
                            <PhoneInput id="contact-phone" value={formData.phone || ""} onValueChange={(val) => setFormData((p) => ({ ...p, phone: val }))} defaultCountry="KZ" allowedCountries={["KZ", "RU", "US"]} wrapperClassName="w-full" />
                        </div>
                        <div className="space-y-2">
                            <Label>{t(Namespace.PickupLocation, "email")}</Label>
                            <Input value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} />
                        </div>
                        <DialogFooter className="flex sm:justify-between w-full items-center">
                            {editingContact ? (
                                <Button
                                    id="edit-pickup-location-archive-button"
                                    type="button"
                                    variant="opacityRedWithOutline"
                                    onClick={handleDelete}
                                    disabled={isLoadingGlobal}
                                >
                                    {t(Namespace.PickupLocation, "delete")}
                                </Button>
                            ) : (
                                <div />
                            )}
                            <div className="flex items-center gap-2">
                                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} disabled={isLoadingGlobal}>
                                    {t(Namespace.PickupLocation, "cancel")}
                                </Button>
                                <Button type="submit" disabled={isLoadingGlobal}>
                                    {t(Namespace.PickupLocation, "save")}
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};